import { call, put, takeLatest, all, select } from 'redux-saga/effects';
import {
  PCMS_AMDSM_REQUEST,
  PCMS_CARE_ASSIST_REQUEST,
  PCMS_CARE_ASSIST_TIN_ACTIVATIONS_DETAILS_REQUEST,
  PCMS_SOT_REQUEST,
  PCMS_DETAILS_REQUEST,
  PCMS_FILTER_DATA_REQUEST,
} from '../../../../actions/actionTypes';
import {
  AMDSMDDataLoading,
  AMDSMDDataSuccess,
  AMDSMDDataError,
  SOTDataLoading,
  SOTDataSuccess,
  SOTDataError,
  CareAssistDataLoading,
  CareAssistDataError,
  CareAssistDataSuccess,
  CareAssistTinActivationsDetailsLoading,
  CareAssistTinActivationsDetailsSuccess,
  CareAssistTinActivationsDetailsError,
  PCMSTableDataLoading,
  PCMSTableDataSuccess,
  PCMSTableDataError,
  pcmsFilterDataLoading,
  pcmsFilterDataSuccess,
  pcmsFilterDataError,
} from '../../../../actions/ServiceInteraction/DigitalSolutions/preCheckMyScript';
import {
  getAMDSMData,
  getSOTData,
  getPcmsMetricDetails,
  getPcmsFilterData,
} from '../../../../services/serviceInteraction/digitalSummary/preCheckMycript/preCheckMyScript.service';
import { getCareAssistData, getCareAssistTinActivationsDetailsData } from '@src/middleware/services/serviceInteraction/digitalSummary/preCheckMycript/preCheckMyScript.service';

const GEO_TAB_RESPONSE_KEY_MAP: Record<string, string> = {
  LOB: 'LOB',
  TIN: 'TIN',
  NPI: 'NPI',
  SPECIALITY: 'SPECIALITY',
  STATE: 'STATES',
  MAJORMARKETNAME: 'MARKETS',
};

export function* fetchAMDSMD(action: any) {
  try {
    yield put(AMDSMDDataLoading(true));

    // Call the API services
    const response = yield call(getAMDSMData, action?.payload);
    // Reset loading state
    yield put(AMDSMDDataLoading(false));

    if (!response || response?.Status !== 'OK') {
      // Handle error
      yield put(AMDSMDDataError('Failed to fetch data'));
    } else {
      // Handle success - combine both responses
      const Data = response;
      yield put(AMDSMDDataSuccess(Data));
    }
  } catch (error) {
    yield put(AMDSMDDataLoading(false));
    yield put(AMDSMDDataError('An error occurred'));
  }
}

export function* fetchSOT(action: any) {
  try {
    yield put(SOTDataLoading(true));

    // Call the API services
    const response = yield call(getSOTData, action?.payload);
    // Reset loading state
    yield put(SOTDataLoading(false));

    if (!response || response?.Status !== 'OK') {
      // Handle error
      yield put(SOTDataError('Failed to fetch data'));
    } else {
      // Handle success - combine both responses
      const Data = response;
      yield put(SOTDataSuccess(Data));
    }
  } catch (error) {
    yield put(SOTDataLoading(false));
    yield put(SOTDataError('An error occurred'));
  }
}

export function* fetchCareAssist(action: any) {
  try {
    yield put(CareAssistDataLoading(true));

    // Call the API services
    const response = yield call(getCareAssistData, action?.payload);
    const Data = response.Data;
    yield put(CareAssistDataSuccess(Data));
  } catch (error) {
    yield put(CareAssistDataError('An error occurred'));
  } finally {
    yield put(CareAssistDataLoading(false));
  }
}

export function* fetchCareAssistTinActivationsDetails(action: any) {
  try {
    yield put(CareAssistTinActivationsDetailsLoading(true));

    // Call the API services
    const response = yield call(getCareAssistTinActivationsDetailsData, action?.payload);
    const Data = response.Data;
    yield put(CareAssistTinActivationsDetailsSuccess(Data));
  } catch (error) {
    yield put(CareAssistTinActivationsDetailsError('An error occurred'));
  } finally {
    yield put(CareAssistTinActivationsDetailsLoading(false));
  }
}
export function* fetchMetricDetails(action: any) {
  try {
    yield put(PCMSTableDataLoading(true));

    let geoCriteria: string | null = null;
    try {
      geoCriteria = typeof window !== 'undefined' ? window.sessionStorage.getItem('GeoCriteria') : null;
    } catch {
      geoCriteria = null;
    }
    const hasGeoCriteria = geoCriteria !== null && geoCriteria !== 'null';
    let isPcmsBulkFlow = false;
     try {
       isPcmsBulkFlow = typeof window !== 'undefined' &&
         window.sessionStorage.getItem('contractIdNavigation') === 'true' &&
         window.sessionStorage.getItem('hiVolumeContractFlow') === 'true';
     } catch {
       isPcmsBulkFlow = false;
     }
    const { requestType, ...metricPayload } = action?.payload || {};

    if (!hasGeoCriteria && !isPcmsBulkFlow) {
      const metricRequests = [
        call(getPcmsMetricDetails, metricPayload, 'LOB'),
        call(getPcmsMetricDetails, metricPayload, 'TIN'),
        call(getPcmsMetricDetails, metricPayload, 'NPI'),
        call(getPcmsMetricDetails, metricPayload, 'SPECIALITY'),
      ];

      const [lobResponse, tinResponse, npiResponse, specialityResponse] = yield all(metricRequests);

      if (!lobResponse || lobResponse?.Status !== 'OK' ||
          !tinResponse || tinResponse?.Status !== 'OK' ||
          !npiResponse || npiResponse?.Status !== 'OK' ||
          !specialityResponse || specialityResponse?.Status !== 'OK') {
        yield put(PCMSTableDataError('Failed to fetch data'));
        yield put(PCMSTableDataLoading(false));
        return;
      }

      const Data = {
        LOB: lobResponse,
        TIN: tinResponse,
        NPI: npiResponse,
        SPECIALITY: specialityResponse,
      };
      yield put(PCMSTableDataSuccess(Data));
      return;
    }

    const normalizedRequestType = typeof requestType === 'string' && requestType
      ? requestType.toUpperCase()
      : 'LOB';
    const responseKey = GEO_TAB_RESPONSE_KEY_MAP[normalizedRequestType] || 'LOB';
    const metricResponse = yield call(getPcmsMetricDetails, metricPayload, normalizedRequestType);

    if (!metricResponse || metricResponse?.Status !== 'OK') {
      yield put(PCMSTableDataError('Failed to fetch data'));
      yield put(PCMSTableDataLoading(false));
      return;
    }

    const existingTableDetails = yield select(
      (state: any) => state?.PreCheckMyScriptReducer?.pcmsTableDetails || {}
    );
    const isTabSpecificRequest = typeof requestType === 'string' && requestType.trim().length > 0;
    const baseTableDetails = isTabSpecificRequest ? existingTableDetails : {};

    // Merge tab-level responses for tab clicks; reset on initial/filter-driven reloads.
    const Data = {
      ...baseTableDetails,
      [responseKey]: metricResponse,
    };
    yield put(PCMSTableDataSuccess(Data));
  } catch (error) {
    yield put(PCMSTableDataError('An error occurred'));
  } finally {
    // Reset loading state in all cases
    yield put(PCMSTableDataLoading(false));
  }
}

export function* fetchPcmsFilterDetails(action: any) {
  try {
    yield put(pcmsFilterDataLoading(true));

    const response = yield call(getPcmsFilterData, action?.payload);
    const pcmsFilterPayload = action?.payload?.PcmsFilter || {};
    const isGeoFlowRequest =
      !pcmsFilterPayload?.Tin &&
      !pcmsFilterPayload?.ProviderSysKey;
    const rawTimeSources = response?.Data?.PCMSTimeSources || response?.Data?.pCMSTimeSources;
    const normalizedTimeSources = Array.isArray(rawTimeSources)
      ? rawTimeSources.map((source: any) => ({
        Default: source?.Default ?? source?.default ?? false,
        StartDate: source?.StartDate || source?.startDate || '',
        EndDate: source?.EndDate || source?.endDate || '',
        FilterText: source?.FilterText || source?.filterText || '',
        Feature: source?.Feature || source?.feature || 'PCMS',
      }))
      : [];

    yield put(pcmsFilterDataLoading(false));

    if (!response || (!isGeoFlowRequest && response?.Status !== 'OK') || normalizedTimeSources.length === 0) {
      yield put(pcmsFilterDataError('Failed to fetch PCMS filter data'));
    } else {
      const filterData = {
        dateFilter: normalizedTimeSources,
        topFilter: isGeoFlowRequest ? [] : (response?.Data?.ProviderTinsAndNpis || []),
      };
      yield put(pcmsFilterDataSuccess(filterData));
    }
  } catch (error) {
    yield put(pcmsFilterDataLoading(false));
    yield put(pcmsFilterDataError('An error occurred'));
  }
}

export function* PreCheckMyScriptSaga() {
  yield takeLatest(PCMS_AMDSM_REQUEST, fetchAMDSMD);
  yield takeLatest(PCMS_SOT_REQUEST, fetchSOT);
  yield takeLatest(PCMS_CARE_ASSIST_REQUEST, fetchCareAssist);
  yield takeLatest(PCMS_CARE_ASSIST_TIN_ACTIVATIONS_DETAILS_REQUEST, fetchCareAssistTinActivationsDetails);
  yield takeLatest(PCMS_DETAILS_REQUEST, fetchMetricDetails);
  yield takeLatest(PCMS_FILTER_DATA_REQUEST, fetchPcmsFilterDetails);
}

export default [PreCheckMyScriptSaga];
